import type { Project } from '@enersu/core-orchestrator';
import type { GenericAnalysisOutput } from '@enersu/report-generator';

export interface PilotBackendClient {
  savePipelineRun(input: SavePilotPipelineRunInput): Promise<PilotBackendSaveResult>;
}

export interface SavePilotPipelineRunInput {
  project: Project;
  analyses: readonly GenericAnalysisOutput[];
  failed: number;
  needsReview: number;
}

export interface PilotBackendSaveResult {
  projectId: string;
  pipelineRunId: string;
  reportId: string;
}

export interface CreateHttpPilotBackendClientOptions {
  apiKey: string;
  baseUrl: string;
  fetcher?: typeof fetch;
}

interface BackendProjectResponse {
  id: string;
}

interface BackendPipelineRunResponse {
  id: string;
}

interface BackendReportResponse {
  id: string;
}

interface ViteBackendEnv {
  VITE_ENERSU_API_KEY?: string;
  VITE_ENERSU_BACKEND_URL?: string;
}

export function createConfiguredPilotBackendClient(): PilotBackendClient | undefined {
  const env = (import.meta as ImportMeta & { readonly env?: ViteBackendEnv }).env;
  const apiKey = env?.VITE_ENERSU_API_KEY;
  const baseUrl = env?.VITE_ENERSU_BACKEND_URL;

  if (apiKey === undefined || baseUrl === undefined) {
    return undefined;
  }

  return createHttpPilotBackendClient({ apiKey, baseUrl });
}

export function createHttpPilotBackendClient(
  options: CreateHttpPilotBackendClientOptions,
): PilotBackendClient {
  const baseUrl = options.baseUrl.replace(/\/+$/, '');
  const fetcher = options.fetcher ?? fetch;

  return {
    async savePipelineRun(input) {
      const project = await postJson<BackendProjectResponse>(
        fetcher,
        baseUrl,
        options.apiKey,
        '/projects',
        {
          formaProjectId: input.project.id,
          municipalityId: input.project.municipalityId,
          name: input.project.name,
          timezone: input.project.timezone,
        },
      );
      const pipelineRun = await postJson<BackendPipelineRunResponse>(
        fetcher,
        baseUrl,
        options.apiKey,
        '/pipeline-runs',
        {
          projectId: project.id,
          requestedStudies: input.analyses.map((analysis) => analysis.id),
          summary: {
            analyses: input.analyses.length,
            failed: input.failed,
            needsReview: input.needsReview,
            source: 'forma-extension',
          },
        },
      );

      await patchJson<BackendPipelineRunResponse>(
        fetcher,
        baseUrl,
        options.apiKey,
        `/pipeline-runs/${pipelineRun.id}`,
        {
          status: 'complete',
          summary: {
            analyses: input.analyses.length,
            failed: input.failed,
            needsReview: input.needsReview,
            source: 'forma-extension',
          },
        },
      );

      const report = await postJson<BackendReportResponse>(
        fetcher,
        baseUrl,
        options.apiKey,
        '/reports',
        {
          language: 'he',
          metadata: {
            analyses: input.analyses.length,
            needsReview: input.needsReview,
          },
          pipelineRunId: pipelineRun.id,
          projectId: project.id,
          status: 'draft',
        },
      );

      return {
        pipelineRunId: pipelineRun.id,
        projectId: project.id,
        reportId: report.id,
      };
    },
  };
}

async function postJson<ResponseBody>(
  fetcher: typeof fetch,
  baseUrl: string,
  apiKey: string,
  path: string,
  payload: unknown,
): Promise<ResponseBody> {
  return requestJson(fetcher, baseUrl, apiKey, path, 'POST', payload);
}

async function patchJson<ResponseBody>(
  fetcher: typeof fetch,
  baseUrl: string,
  apiKey: string,
  path: string,
  payload: unknown,
): Promise<ResponseBody> {
  return requestJson(fetcher, baseUrl, apiKey, path, 'PATCH', payload);
}

async function requestJson<ResponseBody>(
  fetcher: typeof fetch,
  baseUrl: string,
  apiKey: string,
  path: string,
  method: 'PATCH' | 'POST',
  payload: unknown,
): Promise<ResponseBody> {
  const response = await fetcher(`${baseUrl}${path}`, {
    body: JSON.stringify(payload),
    headers: {
      'content-type': 'application/json',
      'x-api-key': apiKey,
    },
    method,
  });

  if (!response.ok) {
    throw new Error(`Backend request failed with ${response.status}.`);
  }

  return response.json() as Promise<ResponseBody>;
}
